/**
 * Mock Recommendations for Demo Scenarios
 *
 * Pre-built recommendation data keyed by scenario ID. Each scenario gets
 * contextually plausible recommendations that match:
 *   1. The note's content (domain-relevant suggestions)
 *   2. The intent-aware strategy from the worker task (INTENT_STRATEGIES)
 *
 * Intent → rec_type mapping (mirrors worker/tasks/recommend.py):
 *   SIMPLE_NOTE  → [RELATED_NOTE] (RAG-only, no LLM)
 *   SEARCH_ONLY  → [RELATED_NOTE] (RAG-only, no LLM)
 *   SUMMARIZE    → [RELATED_NOTE, RESEARCH, TOPIC_EXPANSION]
 *   EXTRACT      → [RELATED_NOTE, ACTION_ITEM]
 *   REASON       → [RELATED_NOTE, RESEARCH, TOPIC_EXPANSION]
 *
 * injection-blocked is excluded — pipeline fails at GUARDRAILS,
 * so RECOMMEND stage is never reached.
 */

import type { Recommendation } from "../types/recommendation";

/** Helper to generate a deterministic UUID-like string for demo IDs */
function demoId(scenario: string, index: number): string {
  return `demo-rec-${scenario}-${index}`;
}

const NOW = new Date().toISOString();

/**
 * Mock recommendations keyed by demo scenario ID.
 * Each array contains 3-5 recommendations matching the scenario's
 * intent strategy and note content.
 */
export const MOCK_RECOMMENDATIONS: Record<string, Recommendation[]> = {
  /**
   * simple-note (SIMPLE_NOTE intent) — RAG-only strategy
   * Only RELATED_NOTE recs (no LLM call, modelName = "rag-only")
   */
  "simple-note": [
    {
      id: demoId("simple-note", 1),
      noteId: "demo",
      type: "RELATED_NOTE",
      content: {
        related_note_id: "note-k8s-rbac-audit",
        similarity_score: 0.87,
        explanation:
          "Previous note about Kubernetes RBAC audit findings from last quarter's security review",
      },
      sourceNoteIds: ["note-k8s-rbac-audit"],
      modelName: "rag-only",
      rating: null,
      ratedAt: null,
      createdAt: NOW,
    },
    {
      id: demoId("simple-note", 2),
      noteId: "demo",
      type: "RELATED_NOTE",
      content: {
        related_note_id: "note-sprint-planning-k8s",
        similarity_score: 0.72,
        explanation:
          "Sprint planning note that mentions RBAC configuration as a backlog item",
      },
      sourceNoteIds: ["note-sprint-planning-k8s"],
      modelName: "rag-only",
      rating: null,
      ratedAt: null,
      createdAt: NOW,
    },
    {
      id: demoId("simple-note", 3),
      noteId: "demo",
      type: "RELATED_NOTE",
      content: {
        related_note_id: "note-k8s-security-policies",
        similarity_score: 0.65,
        explanation:
          "Kubernetes Pod Security Standards document covering network policies and RBAC",
      },
      sourceNoteIds: ["note-k8s-security-policies"],
      modelName: "rag-only",
      rating: null,
      ratedAt: null,
      createdAt: NOW,
    },
  ],

  /**
   * pii-detected (SUMMARIZE intent) — RAG + LLM strategy
   * RELATED_NOTE + RESEARCH + TOPIC_EXPANSION
   */
  "pii-detected": [
    {
      id: demoId("pii-detected", 1),
      noteId: "demo",
      type: "RELATED_NOTE",
      content: {
        related_note_id: "note-q3-budget-review",
        similarity_score: 0.81,
        explanation:
          "Q3 budget review meeting notes with overlapping attendees and discussion topics",
      },
      sourceNoteIds: ["note-q3-budget-review"],
      modelName: "rag-only",
      rating: null,
      ratedAt: null,
      createdAt: NOW,
    },
    {
      id: demoId("pii-detected", 2),
      noteId: "demo",
      type: "RESEARCH",
      content: {
        suggestion:
          "Investigate PII handling policies for meeting transcriptions",
        reasoning:
          "This note contained 3 PII entities (name, email, SSN) that were masked. Consider establishing a pre-meeting protocol for sensitive data handling.",
        keywords: ["PII policy", "data governance", "meeting protocol"],
      },
      sourceNoteIds: ["note-q3-budget-review", "note-data-governance"],
      modelName: "gemini-2.0-flash",
      rating: null,
      ratedAt: null,
      createdAt: NOW,
    },
    {
      id: demoId("pii-detected", 3),
      noteId: "demo",
      type: "TOPIC_EXPANSION",
      content: {
        topic: "Automated PII redaction in enterprise meeting workflows",
        questions: [
          "How can meeting recording tools auto-mask PII before storage?",
          "What compliance frameworks (GDPR, CCPA) apply to meeting notes with PII?",
          "Should PII-heavy notes have restricted access controls?",
        ],
        reasoning:
          "The high PII density in this note suggests a systemic gap in how sensitive meeting data is captured and stored.",
      },
      sourceNoteIds: ["note-data-governance"],
      modelName: "gemini-2.0-flash",
      rating: null,
      ratedAt: null,
      createdAt: NOW,
    },
  ],

  /**
   * full-pipeline (REASON intent) — RAG + LLM strategy
   * RELATED_NOTE + RESEARCH + TOPIC_EXPANSION (full set)
   */
  "full-pipeline": [
    {
      id: demoId("full-pipeline", 1),
      noteId: "demo",
      type: "RELATED_NOTE",
      content: {
        related_note_id: "note-grpc-vs-rest-analysis",
        similarity_score: 0.92,
        explanation:
          "Earlier analysis of gRPC adoption for the payment service, including latency benchmarks",
      },
      sourceNoteIds: ["note-grpc-vs-rest-analysis"],
      modelName: "rag-only",
      rating: null,
      ratedAt: null,
      createdAt: NOW,
    },
    {
      id: demoId("full-pipeline", 2),
      noteId: "demo",
      type: "RESEARCH",
      content: {
        suggestion:
          "Benchmark gRPC vs REST with OpenTelemetry trace propagation overhead",
        reasoning:
          "The analysis mentions observability as a factor but doesn't quantify the instrumentation overhead. gRPC interceptors and REST middleware add different latency profiles under OTel.",
        keywords: [
          "gRPC interceptors",
          "OTel propagation",
          "latency benchmarks",
          "protobuf",
        ],
      },
      sourceNoteIds: ["note-grpc-vs-rest-analysis", "note-otel-tracing-setup"],
      modelName: "gemini-2.0-flash",
      rating: null,
      ratedAt: null,
      createdAt: NOW,
    },
    {
      id: demoId("full-pipeline", 3),
      noteId: "demo",
      type: "TOPIC_EXPANSION",
      content: {
        topic:
          "Service mesh as an alternative to per-service protocol decisions",
        questions: [
          "Could a service mesh (Istio/Linkerd) abstract away the gRPC vs REST choice at the transport layer?",
          "What are the operational trade-offs of mesh-level mTLS vs application-level TLS?",
          "How does service mesh observability compare to manual OTel instrumentation?",
        ],
        reasoning:
          "The note focuses on per-service protocol choice, but a mesh could decouple transport from application logic entirely — worth exploring as a higher-level architectural pattern.",
      },
      sourceNoteIds: ["note-service-mesh-exploration"],
      modelName: "gemini-2.0-flash",
      rating: null,
      ratedAt: null,
      createdAt: NOW,
    },
    {
      id: demoId("full-pipeline", 4),
      noteId: "demo",
      type: "RESEARCH",
      content: {
        suggestion:
          "Review how Uber's Schemaless migration informed their gRPC adoption",
        reasoning:
          "Uber's engineering blog documents a similar gRPC migration at scale with concrete latency and developer experience metrics — directly applicable to the trade-off analysis.",
        keywords: [
          "Uber engineering",
          "gRPC migration",
          "schema evolution",
          "backward compatibility",
        ],
      },
      sourceNoteIds: ["note-grpc-vs-rest-analysis"],
      modelName: "gemini-2.0-flash",
      rating: null,
      ratedAt: null,
      createdAt: NOW,
    },
  ],

  /**
   * link-extraction (EXTRACT intent) — RAG + LLM strategy
   * RELATED_NOTE + ACTION_ITEM
   */
  "link-extraction": [
    {
      id: demoId("link-extraction", 1),
      noteId: "demo",
      type: "RELATED_NOTE",
      content: {
        related_note_id: "note-k8s-pod-security-standards",
        similarity_score: 0.89,
        explanation:
          "Existing note on Pod Security Standards that covers complementary RBAC practices",
      },
      sourceNoteIds: ["note-k8s-pod-security-standards"],
      modelName: "rag-only",
      rating: null,
      ratedAt: null,
      createdAt: NOW,
    },
    {
      id: demoId("link-extraction", 2),
      noteId: "demo",
      type: "ACTION_ITEM",
      content: {
        action:
          "Audit current ClusterRoleBindings against the RBAC good practices checklist",
        priority: "high",
        context:
          "The extracted article outlines 8 RBAC best practices — cross-reference against the production cluster's current bindings to identify gaps.",
      },
      sourceNoteIds: ["note-k8s-pod-security-standards"],
      modelName: "gemini-2.0-flash",
      rating: null,
      ratedAt: null,
      createdAt: NOW,
    },
    {
      id: demoId("link-extraction", 3),
      noteId: "demo",
      type: "ACTION_ITEM",
      content: {
        action: "Enable Kubernetes audit logging for RBAC-related API calls",
        priority: "medium",
        context:
          "The article recommends monitoring RBAC changes via audit logs. Currently the cluster has audit logging disabled for non-write operations.",
      },
      sourceNoteIds: [],
      modelName: "gemini-2.0-flash",
      rating: null,
      ratedAt: null,
      createdAt: NOW,
    },
    {
      id: demoId("link-extraction", 4),
      noteId: "demo",
      type: "RELATED_NOTE",
      content: {
        related_note_id: "note-rbac-incident-jan",
        similarity_score: 0.74,
        explanation:
          "January incident report where overly broad ClusterRole caused unauthorized namespace access",
      },
      sourceNoteIds: ["note-rbac-incident-jan"],
      modelName: "rag-only",
      rating: null,
      ratedAt: null,
      createdAt: NOW,
    },
  ],

  /**
   * audio-transcription (SUMMARIZE intent) — RAG + LLM strategy
   * RELATED_NOTE + RESEARCH + TOPIC_EXPANSION
   */
  "audio-transcription": [
    {
      id: demoId("audio-transcription", 1),
      noteId: "demo",
      type: "RELATED_NOTE",
      content: {
        related_note_id: "note-prev-audio-transcript",
        similarity_score: 0.78,
        explanation:
          "Previous audio transcription from the same meeting series with overlapping topics",
      },
      sourceNoteIds: ["note-prev-audio-transcript"],
      modelName: "rag-only",
      rating: null,
      ratedAt: null,
      createdAt: NOW,
    },
    {
      id: demoId("audio-transcription", 2),
      noteId: "demo",
      type: "RESEARCH",
      content: {
        suggestion:
          "Compare Whisper transcription accuracy across meeting acoustic environments",
        reasoning:
          "Audio transcription quality varies significantly with background noise and speaker count. Benchmarking against different meeting formats would improve pipeline reliability.",
        keywords: ["Whisper accuracy", "WER benchmarks", "noise robustness"],
      },
      sourceNoteIds: ["note-whisper-setup"],
      modelName: "gemini-2.0-flash",
      rating: null,
      ratedAt: null,
      createdAt: NOW,
    },
    {
      id: demoId("audio-transcription", 3),
      noteId: "demo",
      type: "TOPIC_EXPANSION",
      content: {
        topic: "Speaker diarization for multi-participant meeting notes",
        questions: [
          "Could pyannote.audio add speaker labels to improve note organization?",
          "How would speaker-attributed transcripts change the summarization prompt?",
          "What privacy implications does speaker identification introduce?",
        ],
        reasoning:
          "The current pipeline treats all speech as one stream. Speaker diarization would enable per-person action items and more structured meeting summaries.",
      },
      sourceNoteIds: ["note-whisper-setup", "note-prev-audio-transcript"],
      modelName: "gemini-2.0-flash",
      rating: null,
      ratedAt: null,
      createdAt: NOW,
    },
  ],

  /**
   * rag-search (SEARCH_ONLY intent) — RAG-only strategy
   * Only RELATED_NOTE recs (no LLM call, modelName = "rag-only")
   */
  "rag-search": [
    {
      id: demoId("rag-search", 1),
      noteId: "demo",
      type: "RELATED_NOTE",
      content: {
        related_note_id: "note-k8s-pod-security",
        similarity_score: 0.94,
        explanation:
          "Comprehensive notes on Kubernetes Pod Security Standards including admission controllers and security contexts",
      },
      sourceNoteIds: ["note-k8s-pod-security"],
      modelName: "rag-only",
      rating: null,
      ratedAt: null,
      createdAt: NOW,
    },
    {
      id: demoId("rag-search", 2),
      noteId: "demo",
      type: "RELATED_NOTE",
      content: {
        related_note_id: "note-rbac-config-guide",
        similarity_score: 0.88,
        explanation:
          "Step-by-step RBAC configuration guide with ClusterRole and RoleBinding examples",
      },
      sourceNoteIds: ["note-rbac-config-guide"],
      modelName: "rag-only",
      rating: null,
      ratedAt: null,
      createdAt: NOW,
    },
    {
      id: demoId("rag-search", 3),
      noteId: "demo",
      type: "RELATED_NOTE",
      content: {
        related_note_id: "note-k8s-network-policies",
        similarity_score: 0.71,
        explanation:
          "NetworkPolicy configurations that complement RBAC by enforcing pod-to-pod communication rules",
      },
      sourceNoteIds: ["note-k8s-network-policies"],
      modelName: "rag-only",
      rating: null,
      ratedAt: null,
      createdAt: NOW,
    },
  ],
};

/**
 * Get mock recommendations for a scenario ID.
 * Returns null for scenarios that don't generate recommendations
 * (e.g., injection-blocked where pipeline fails at GUARDRAILS).
 */
export function getMockRecommendations(
  scenarioId: string,
): Recommendation[] | null {
  return MOCK_RECOMMENDATIONS[scenarioId] ?? null;
}
